/**
 * Thème d'affichage (Profil › Préférences › Apparence).
 *
 * Le choix vit en `localStorage`, pas côté serveur : il doit être lu AVANT le
 * premier rendu pour éviter un flash clair, ce qu'un aller-retour réseau ne
 * permet pas. Le script en tête de `app/layout.tsx` fait cette première lecture
 * de façon synchrone ; ce module est la version typée utilisée ensuite par l'app.
 *
 * L'état effectif n'est porté que par l'attribut `data-theme` sur <html> : rien
 * n'est dupliqué dans un état React, donc rien ne peut désynchroniser.
 */

export type ThemeChoice = "clair" | "sombre" | "systeme";

/** Doit rester identique à la clé lue par le script anti-flash du layout. */
export const THEME_STORAGE_KEY = "pharmacowork.theme";

export const THEME_CHOICES: ThemeChoice[] = ["clair", "sombre", "systeme"];

export const THEME_LABELS: Record<ThemeChoice, string> = {
  clair: "Clair",
  sombre: "Sombre",
  systeme: "Système",
};

export const THEME_HELP: Record<ThemeChoice, string> = {
  clair: "Toujours en clair, quel que soit le réglage de l'appareil.",
  sombre: "Toujours en sombre, quel que soit le réglage de l'appareil.",
  systeme: "Suit le réglage d'apparence de votre appareil.",
};

const DARK_QUERY = "(prefers-color-scheme: dark)";

function isThemeChoice(value: unknown): value is ThemeChoice {
  return value === "clair" || value === "sombre" || value === "systeme";
}

/** Le réglage d'apparence de l'appareil. `false` hors navigateur. */
function systemPrefersDark(): boolean {
  if (typeof window === "undefined" || !window.matchMedia) return false;
  return window.matchMedia(DARK_QUERY).matches;
}

export function resolveTheme(choice: ThemeChoice): "light" | "dark" {
  if (choice === "clair") return "light";
  if (choice === "sombre") return "dark";
  return systemPrefersDark() ? "dark" : "light";
}

/** Pose le thème effectif sur <html> — seule source de vérité de l'affichage. */
export function applyTheme(choice: ThemeChoice): void {
  if (typeof document === "undefined") return;
  document.documentElement.dataset.theme = resolveTheme(choice);
}

// ── Store minimal pour useSyncExternalStore ──
// Un état React aurait imposé un `setState` dans un effet au montage, que la
// règle react-hooks/set-state-in-effect interdit — et qui provoquerait de toute
// façon un rendu en thème par défaut avant correction.

const listeners = new Set<() => void>();

function emit(): void {
  for (const listener of listeners) listener();
}

export function subscribeThemeChoice(onChange: () => void): () => void {
  listeners.add(onChange);
  return () => listeners.delete(onChange);
}

/**
 * Instantané côté client. Renvoie une chaîne primitive : `Object.is` la compare
 * par valeur, donc React ne voit pas de changement tant que le choix est stable
 * (exigence de useSyncExternalStore).
 */
export function getThemeChoice(): ThemeChoice {
  if (typeof window === "undefined") return "systeme";
  try {
    const stored = window.localStorage.getItem(THEME_STORAGE_KEY);
    return isThemeChoice(stored) ? stored : "systeme";
  } catch {
    // Stockage refusé (navigation privée stricte) : on reste sur « système ».
    return "systeme";
  }
}

/** Instantané côté serveur : le prérendu ne connaît aucun choix. */
export function getServerThemeChoice(): ThemeChoice {
  return "systeme";
}

export function setThemeChoice(choice: ThemeChoice): void {
  try {
    window.localStorage.setItem(THEME_STORAGE_KEY, choice);
  } catch {
    // Choix non persistable : il s'applique quand même pour cette session.
  }
  applyTheme(choice);
  emit();
}

/** Réapplique le thème courant — utile quand l'OS change d'apparence. */
export function reapplyTheme(): void {
  applyTheme(getThemeChoice());
}
